export default class LoadMore extends HTMLElement {
  constructor() {
    super();
    this.isInitialized = false;
    this.observer = null;
    this.init();
    this.bindFacetEvents();
  }

  init() {
    if (this.button) {
      this.button.removeEventListener('click', this.boundLoadMoreProducts);
    }

    this.button = document.querySelector('.load-more-button');
    if (!this.button) return;

    this.productGrid = document.querySelector('#product-grid');
    this.loadingSpinner = document.querySelector('.loading-spinner');
    this.loadingIcon = document.querySelector('.loading-icon');
    this.spinnerVideo = this.loadingSpinner?.querySelector('video');
    this.buttonLabel = this.button.querySelector('.label');

    this.boundLoadMoreProducts = this.loadMoreProducts.bind(this);
    this.button.addEventListener('click', this.boundLoadMoreProducts);
    this.rangeIndicator = document.querySelector('#product-range-indicator');

    this.updateRangeIndicator();

    const scrollPosition = sessionStorage.getItem('scrollPosition');
    if (scrollPosition) {
      const collectionHandle = window.location.pathname?.split('/')?.pop();
      const [savedHandle, scrollY] = scrollPosition.split(':');
      if (savedHandle === collectionHandle) {
        window.scrollTo({
          left: 0, 
          top: parseInt(scrollY, 10), 
          behavior: 'smooth'
        });
      }
    }

    window.addEventListener('scroll', (e) => {
      const collectionHandle = window.location.pathname?.split('/')?.pop();
      if (collectionHandle) {
        sessionStorage.setItem('scrollPosition', `${collectionHandle}:${window.scrollY}`);
      }
    });

    this.isInitialized = true;
  }

    updateRangeIndicator() {
      if (!this.rangeIndicator || !this.productGrid) {
        console.warn('LoadMore: Range indicator or product grid not found');
        return;
      }

      // Count currently displayed products
      const currentProducts = this.productGrid.querySelectorAll('.product-card, .card-wrapper, [class*="product-item"]').length;
      // Get total products from button data attribute
      const totalProducts = parseInt(this.button?.dataset.totalProducts || 0);
      if (currentProducts > 0 && totalProducts > 0 && currentProducts < totalProducts) {
        const startRange = 1;
        const endRange = Math.min(currentProducts, totalProducts);
        this.rangeIndicator.textContent = `${startRange} – ${endRange} of ${totalProducts}`;
        this.rangeIndicator.classList.remove('hidden');
      } else {
        this.rangeIndicator.classList.add('hidden');
      }
    }

  bindFacetEvents() {
    if (this.hasBoundEvents) return;
    this.hasBoundEvents = true;

    document.addEventListener('facet:update:complete', () => {
      this.init();
    });

    const productGridContainer = document.getElementById(
      'ProductGridContainer',
    );
    if (productGridContainer && !this.observer) {
      if (this.observer) {
        this.observer.disconnect();
      }

      this.observer = new MutationObserver((mutations) => {
        // Only reinitialize if not already in the process of loading more products
        if (!this.isLoading && this.button && this.button.disabled === false) {
          this.init();
        }
      });

      this.observer.observe(productGridContainer, {
        childList: true,
        subtree: false,
      });
    }
  }

  async loadMoreProducts(event) {
    event.preventDefault();

    // Prevent multiple simultaneous loads
    if (this.isLoading) return;
    this.isLoading = true;

    const hasSeenLoader = sessionStorage.getItem('hasSeenLoadMoreAnimation');
    const isDesktop = window.innerWidth >= 990;

    if (!this.button || !this.productGrid) {
      this.isLoading = false;
      return;
    }

    // Get current URL with all active filters
    const currentUrl = new URL(window.location.href);
    const searchParams = new URLSearchParams(currentUrl.search);

    const nextPage = parseInt(this.button.dataset.nextPage);
    const totalPages = parseInt(this.button.dataset.totalPages);

    // Show loading state
    this.button.disabled = true;
    this.buttonLabel.classList.add('hidden');
    this.loadingIcon.classList.remove('hidden');

    try {
      let productsData;

      if (!hasSeenLoader && isDesktop && this.loadingSpinner) {
        // Only play animation on desktop devices and if we haven't seen it before
        const [_, fetchedProducts] = await Promise.all([
          this.playLoadingAnimation(),
          this.fetchProducts(currentUrl.pathname, nextPage, searchParams),
        ]);
        productsData = fetchedProducts;
        sessionStorage.setItem('hasSeenLoadMoreAnimation', 'true');
      } else {
        // On mobile or if we've seen the animation before, just fetch products
        productsData = await this.fetchProducts(
          currentUrl.pathname,
          nextPage,
          searchParams,
        );
      }

      // Append the products we already fetched
      this.productGrid.insertAdjacentHTML('beforeend', productsData);

      // Update the progress indicator after loading new products
      this.updateRangeIndicator();

      // Update button state
      this.button.dataset.nextPage = nextPage + 1;
      if (nextPage >= totalPages) {
        this.button.remove();
      }

      // Reinitialize any product card functionality
      if (window.reinitializeProductCard) {
        window.reinitializeProductCard();
      }
    } catch (error) {
      console.error('LoadMore: Error loading products:', error);
    } finally {
      if (this.button) {
        this.button.disabled = false;
        this.buttonLabel?.classList.remove('hidden');
        this.loadingIcon.classList.add('hidden');
        window.history.replaceState({}, '', `?p=${nextPage}`);
      }
      this.isLoading = false;
    }
  }

  async fetchProducts(pathname, nextPage, searchParams) {
    // Keep all existing filter parameters
    searchParams.set('page', nextPage);
    searchParams.set('section_id', this.productGrid.dataset.id);

    const fetchUrl = `${pathname}?${searchParams.toString()}`;

    const response = await fetch(fetchUrl);
    const text = await response.text();
    const parser = new DOMParser();
    const html = parser.parseFromString(text, 'text/html');
    const productGrid = html.querySelector('#product-grid');

    if (!productGrid) {
      console.error('LoadMore: Could not find #product-grid in response');
      return '';
    }

    return productGrid.innerHTML;
  }

  async playLoadingAnimation() {
    return new Promise((resolve) => {
      if (!this.loadingSpinner) {
        resolve();
        return;
      }

      this.loadingSpinner.classList.add('opacity-100');

      if (this.spinnerVideo) {
        this.spinnerVideo.currentTime = 0;
        this.spinnerVideo.play();
      }

      setTimeout(() => {
        this.loadingSpinner.classList.remove('opacity-100');
      }, 3500);

      setTimeout(resolve, 3500);
    });
  }

  disconnectedCallback() {
    if (this.observer) {
      this.observer.disconnect();
    }
    if (this.button) {
      this.button.removeEventListener('click', this.boundLoadMoreProducts);
    }
  }
}

if (!customElements.get('load-more')) {
  customElements.define('load-more', LoadMore);

  window.reinitializeLoadMore = () => {
    document.querySelectorAll('load-more').forEach((element) => {
      element.init();
    });
  };
}
